import { call } from "redux-saga/effects";

import type { Profile } from "@superblocksteam/shared";

import type { PayloadAction } from "core/store/utils/action";

import { createSaga } from "../../../utils/saga";
import { updateProfileAccess } from "../client";
import slice from "../slice";

interface UpdateProfileAccessPayload {
  orgId: string;
  profileId: string;
  accessUpdate: {
    allowedInEditor?: boolean;
    allowedInPreview?: boolean;
    allowedInDeployed?: boolean;
    defaultInEditor?: boolean;
    defaultInPreview?: boolean;
    defaultInDeployed?: boolean;
  };
}

interface UpdateProfileAccessResponse {
  orgId: string;
  profileId: string;
  updated: Profile;
  accessUpdate: UpdateProfileAccessPayload["accessUpdate"];
}

function* updateProfileAccessInternal({
  orgId,
  profileId,
  accessUpdate,
}: UpdateProfileAccessPayload) {
  const profile: Profile = yield call(
    updateProfileAccess,
    orgId,
    profileId,
    accessUpdate,
  );
  const response: UpdateProfileAccessResponse = {
    orgId,
    profileId,
    updated: profile,
    accessUpdate,
  };
  return response;
}

export const updateProfileAccessSaga = createSaga(
  updateProfileAccessInternal,
  "updateProfileAccessSaga",
  { sliceName: "organizations" },
);

slice.saga(updateProfileAccessSaga, {
  start(state, payload: PayloadAction<UpdateProfileAccessPayload>) {
    state.loading[payload.payload.orgId] = true;
    delete state.errors[payload.payload.orgId];
  },
  success(state, { payload }) {
    if (!payload.updated) {
      delete state.loading[payload.orgId];
      return;
    }

    state.entities[payload.orgId] = state.entities[payload.orgId] ?? {};
    const existingProfiles = state.entities[payload.orgId].profiles;
    state.entities[payload.orgId].profiles = existingProfiles?.map(
      (profile) => {
        const withClearedDefaults = {
          ...profile,
          ...(payload.accessUpdate.defaultInEditor === true
            ? { defaultInEditor: false }
            : {}),
          ...(payload.accessUpdate.defaultInPreview === true
            ? { defaultInPreview: false }
            : {}),
          ...(payload.accessUpdate.defaultInDeployed === true
            ? { defaultInDeployed: false }
            : {}),
        };
        if (profile.id === payload.profileId) {
          return {
            ...withClearedDefaults,
            ...payload.updated,
          };
        }
        return withClearedDefaults;
      },
    );
    delete state.loading[payload.orgId];
  },
  error(state, { payload, meta }) {
    state.errors[meta.args.orgId] = { error: payload };
    delete state.loading[meta.args.orgId];
  },
});
